import type { HubInvoicePayload } from "@itaas/insurance-contract";
import {
  encodeInvoiceImport,
  insurerCodeToOrgId,
  invoiceImportUrl,
} from "@itaas/insurance-contract";
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import type { BillingDocument } from "@/core/billing/billing-documents/types";
import { IAM_TENANT_SLUG } from "@/core/config";
import { getTenantDisplayName } from "@/core/tenant-display";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { postInvoiceImportToHubApi } from "./hub-api-client";
import { getInsurerById } from "./insurer-store";
import { defaultInsuranceHubUrl, getActivePartnershipForInsurer } from "./partnership-store";

export interface HubInvoicePublishResult {
  invoiceId: string;
  via: "api" | "url";
  importUrl?: string;
  error?: string;
}

export function buildHubInvoiceForDocument(
  doc: BillingDocument,
): { token: string; importUrl: string; payload: HubInvoicePayload } | null {
  if (doc.payerType !== "insurer" || !doc.insurerId) return null;
  if (doc.status !== "issued") return null;

  const insurer = getInsurerById(doc.insurerId);
  const patient = getPatientById(doc.patientId);
  const partnership = getActivePartnershipForInsurer(doc.insurerId);
  if (!insurer || !patient || !partnership) return null;

  const session = loadStoredSession();
  const tenantSlug = session?.tenant?.slug ?? IAM_TENANT_SLUG;
  const tenantName = getTenantDisplayName();

  const payload: HubInvoicePayload = {
    invoiceId: doc.hubInvoiceId ?? `inv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    partnershipId: partnership.id,
    hospitalTenantSlug: tenantSlug,
    hospitalName: tenantName,
    insurerOrgId: insurerCodeToOrgId(insurer.code),
    billingDocumentId: doc.id,
    patientId: patient.id,
    patientName: patientFullName(patient),
    memberNumber: patient.memberNumber,
    policyNumber: patient.policyNumber,
    invoiceNumber: doc.number,
    lines: doc.lines.map((line) => ({
      label: line.label,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
    })),
    amount: doc.subtotal,
    issuedAt: doc.issuedAt ?? new Date().toISOString(),
  };

  const token = encodeInvoiceImport(payload);
  const hubUrl = defaultInsuranceHubUrl();
  return { token, importUrl: invoiceImportUrl(token, hubUrl), payload };
}

/** Publie la facture assureur sur le hub via API (prioritaire) — sans redirection navigateur. */
export async function publishBillingDocumentToHub(
  doc: BillingDocument,
): Promise<HubInvoicePublishResult | null> {
  const built = buildHubInvoiceForDocument(doc);
  if (!built) return null;

  const apiResult = await postInvoiceImportToHubApi(built.token);
  if (apiResult.ok && apiResult.invoiceId) {
    return { invoiceId: apiResult.invoiceId, via: "api" };
  }

  return {
    invoiceId: built.payload.invoiceId,
    via: "url",
    importUrl: built.importUrl,
    error:
      apiResult.error ??
      "Publication API échouée — vérifiez le partenariat actif sur le hub assureur.",
  };
}

/** Fallback manuel — ouvre l'URL d'import (ancien flux démo navigateur). */
export function openHubInvoiceImportUrl(doc: BillingDocument): string | null {
  const built = buildHubInvoiceForDocument(doc);
  if (!built) return null;
  window.open(built.importUrl, "_blank", "noopener,noreferrer");
  return built.importUrl;
}
